<template>
  <div class="flex flex-col justify-between w-full" @click.stop>
    <div class="flex justify-around items-center bg-primary-600 w-full font-bold rounded-full text-white" :class="height" v-if="cartItemQty > 0">
      <div @click.stop="reduceQty(product.sku_id || product.parent_item_id)" class="cursor-pointer flex items-center justify-center py-2 select-none">
       <V3ImageComponent src="icons/minus.png" class="h-0.5 w-3 bg-white"  />
      </div>
      <span>{{ cartItemQty }}</span>
      <V3ImageComponent src="icons/plus.png" class="h-3 w-3 cursor-pointer" @click.stop="addToCart(product)" />
    </div>
    <UButton v-else color="primary"  class="text-sm md:text-md" :size="size" block variant="outline" :ui="{ variant: { outline: runtimeConfig?.public?.partnerCode?.toLowerCase() === 'oneindiamall' ? 'bg-white' : 'bg-orange-50' } }"
      @click.stop="addToCart(product)">{{ label || 'Add' }}</UButton>
    <div v-if="customize && label.toLowerCase() !== 'customize'" class="w-full flex justify-center py-0.5">
      <p class="text-xs text-info-700">Customize</p>
    </div>
    <V2Overlay v-model="isVariantOpen" :container-class="'pt-6 pr-4 pl-4 pb-1 md:pb-6'">
  <V2VariantsComponent
    :data="props.product"
    @close="isVariantOpen = false"
  />
</V2Overlay>
    <V2Overlay v-model="isOverlayOpen" :container-class="'pt-6 pr-4 pl-4 pb-1 md:pb-6'">
  <V3ReplaceCartComponent
    :is="overlayData.component"
    v-bind="overlayData"
    @success="overlayData.onSuccess"
    @close="overlayData.onClose"
  />
</V2Overlay>
  </div>
</template>

<script setup>
import { V3PopupComponent } from '#components';
import { V3ModalComponent } from '#components';

const { isMobile } = useDevice()
const cart = useCart();
const giftCart = useGiftCart();
const navIcons = useNavIcons();
const props = defineProps({
  product: { type: Object, required: true },
  label: { type: String, default: 'Add' },
  size: { type: String, default: 'md' },
  customize: { type: Boolean, default: false },
  parent: { type: Boolean, default: false },
  variants: { type: Boolean, default: false }
});
const cartField = props.parent ? 'parent_item_id' : 'sku_id';
const cartItems = computed(() => cart.items.filter(item => item[cartField] === props.product[cartField]));
const cartItemQty = computed(() =>
  cartItems.value.reduce((total, item) => total + item.qty, 0)
);
const height = computed(() => props.size === 'xl' ? 'h-10' : 'h-8');

const slideover = useSlideover();
const modal = useModal();
const { isPopupOpen, openPopup, closePopup } = popupBackHandler();
const runtimeConfig = useRuntimeConfig();
const isVariantOpen = ref(false)
const hasVariants = computed(() => {
  return (
    (props.product?.variants && props.product.variants.length > 0) ||
    props.product?.has_variant
  )
})
const openModal = (component, payload, onSuccess) => {
  // openPopup(); // Handles back button history push

  const options = {
    component,
    data: payload,
    onClose: () => {
      if (isMobile.value) {
        slideover.close();
      } else {
        modal.close();
      }
      history.replaceState(null, '', window.location.href); // Clear history entry
    },
  };

  if (onSuccess) {
    options.onSuccess = onSuccess;
  }

  if (isMobile.value) {
    slideover.open(V3PopupComponent, options);
  } else {
    modal.open(V3ModalComponent, options);
  }
};
const isOverlayOpen = ref(false)
const overlayData = ref({})

const openAddCartOverlay = (product) => {
  isOverlayOpen.value = true
  overlayData.value = {
    component: 'addcart',
    data: product,
    onSuccess: () => {
      isOverlayOpen.value = false

      if (giftCart.items.length > 0) {
        giftCart.clear()
        navIcons.getFooterNavIcons()
      }

      addToCart(product) 
    },
    onClose: () => {
      isOverlayOpen.value = false
    }
  }
}

const openCustomizationOverlay = (product) => {
  isOverlayOpen.value = true
  overlayData.value = {
    component: 'customization',
       data: product,
    onClose: () => {
      isOverlayOpen.value = false
    }
  }
}

const addToCart = async (product) => {
  const { provider, items } = cart

  if (
    cartItemQty.value > 0 &&
    cartItemQty.value === cartItems.value[0].stock.maximum
  ) {
    useCartToast({
      description: `You’ve reached the maximum quantity of [${cartItems.value[0].stock.maximum}] allowed for this item : ${product.name}`
    })
    return
  }

  const productCategory = props.product.category.parent.code.toLowerCase()
  const productProviderSrc = props.product.provider?.provider_src?.toLowerCase()

  const hasGiftCartItems = giftCart.items.length > 0
  const hasDifferentLocation =
  items.length > 0 &&
  items[0]?.location?.id &&
  props.product?.location?.id &&
  items[0].location.id !== props.product.location.id

  const hasDifferentProvider =
    provider && provider !== props.product.provider.ukid

  const cartCategories = cart.items.map(item =>
    item.category.parent.code.toLowerCase()
  )

  const hasDifferentCategoryInCart = cartCategories.some(
    cat => cat !== productCategory
  )

  const cartHasOnlyGroceryOrFB = cartCategories.every(cat =>
    ['grocery', 'f&b'].includes(cat)
  )

  const isProductGroceryOrFB = ['grocery', 'f&b'].includes(productCategory)

  const hasSameCategoryProviderSrcMismatch = cart.items.some(item => {
    const itemCategory = item.category.parent.code.toLowerCase()
    const itemProviderSrc =
      item.provider?.provider_src?.toLowerCase()

    if (
      itemProviderSrc === 'nlincs' &&
      productProviderSrc === 'nlincs' &&
      hasDifferentProvider
    ) return true

    if (itemCategory !== productCategory) return false
    if (!itemProviderSrc && !productProviderSrc) return false

    return itemProviderSrc !== productProviderSrc
  })

  const shouldShowAddCartOverlay =
    (
      (provider || hasDifferentLocation) &&
      (cartHasOnlyGroceryOrFB || isProductGroceryOrFB) &&
      hasDifferentCategoryInCart
    ) ||
    (
      hasDifferentProvider &&
      productCategory === 'f&b'
    ) ||
    hasGiftCartItems ||
    hasDifferentCategoryInCart ||
    hasSameCategoryProviderSrcMismatch

  if (shouldShowAddCartOverlay) {
    openAddCartOverlay(product)
    return
  }

  if (props.customize) {
    openCustomizationOverlay(product)
    return
  }

  if (hasVariants.value) {
    isVariantOpen.value = true
    return
  }

  cart.addItem({
    ...props.product,
    parent_item_id: props.product.sku_id
  })

  await cart.checkSellerOffer()
}


// const addToCart = async (product) => {
//   const { provider, items } = cart;

//   if (cartItemQty.value > 0 && cartItemQty.value === cartItems.value[0].stock.maximum) {
//     const message = `You’ve reached the maximum quantity of [${cartItems.value[0].stock.maximum}] allowed for this item: ${product.name}`;
//     openModal('error', { message });
//   }
//   else if (
//     ((provider && provider !== props.product.provider.ukid) ||
//       (items.length > 0 && items[0]?.location?.id !== props.product.location.id) ||
//       giftCart.items.length > 0) &&
//     (
//       (props.product.category.parent.code.toLowerCase() === 'f&b' ||
//         cart.items.every(item => item.category.parent.code.toLowerCase() === 'f&b')) ||
//       (props.product.category.parent.code.toLowerCase() !== 'grocery' &&
//         cart.items.every(item => item.category.parent.code.toLowerCase() === 'grocery') &&
//         cart.items.some(item => item.provider.ukid !== props.product.provider.ukid)) ||
//       (props.product.category.parent.code.toLowerCase() === 'grocery' &&
//         cart.items.every(item => item.category.parent.code.toLowerCase() !== 'grocery'))
//     )
//   ) {
//     openModal('addcart', product, () => {
//       if (giftCart.items.length > 0) {
//         giftCart.clear();
//         navIcons.getTopNavIcons();
//       }
//       addToCart(product); // Retry
//     });
//   }
//   else if (props.customize) {
//     openModal('customization', product);
//   }
//   else if (props.variants) {
//     openModal('variants', product);
//   }
//   else {
//     cart.addItem({ ...props.product, parent_item_id: props.product.sku_id });
//     await cart.checkSellerOffer();
//   }
// };


// // Watch isPopupOpen to close UI components when back button is pressed ---
// watch(isPopupOpen, (newVal) => {
//   if (!newVal) {
//     if (slideover.isOpen.value) slideover.close();
//     if (modal.isOpen.value) modal.close();
//   }
// });
// watchEffect(() => {
//   if (isPopupOpen.value && !modal.isOpen.value && !slideover.isOpen.value) {
//     closePopup(); // ensures we trigger history.back() even if closed by outside click
//   }
// });

const reduceQty = async (parentId) => {
  console.log('reducing:', parentId)
  cart.reduceQty(parentId);
  await cart.checkSellerOffer()
};


</script>
